
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface NewsFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function NewsFormModal({ isOpen, onClose, onSaved }: NewsFormModalProps) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [source, setSource] = useState('');
  const [publishedAt, setPublishedAt] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
  });
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const resetForm = () => {
    setTitle('');
    setUrl('');
    setSource('');
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    setPublishedAt(new Date(now.getTime() - offset).toISOString().slice(0, 16));
    setResult(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setResult({ success: false, message: '제목을 입력해주세요.' });
      return;
    }
    if (!url.trim()) {
      setResult({ success: false, message: 'URL을 입력해주세요.' });
      return;
    }
    if (!source.trim()) {
      setResult({ success: false, message: '출처를 입력해주세요.' });
      return;
    }

    setIsSaving(true);
    setResult(null);

    try {
      const { error } = await supabase.from('news_articles').insert({
        title: title.trim(),
        url: url.trim(),
        source: source.trim(),
        published_at: new Date(publishedAt).toISOString(),
      });

      if (error) {
        throw new Error(error.message);
      }

      setResult({ success: true, message: '뉴스가 성공적으로 추가되었습니다.' });
      onSaved();
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (err: any) {
      setResult({ success: false, message: err.message || '저장에 실패했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
            <i className="ri-newspaper-line text-xl text-teal-600"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">뉴스 수동 입력</h3>
            <p className="text-xs text-gray-400 mt-0.5">뉴스 기사를 직접 추가하세요</p>
          </div>
          <button
            onClick={handleClose}
            className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-400"></i>
          </button>
        </div>

        <div className="space-y-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">기사 제목 <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent placeholder:text-gray-300"
              placeholder="뉴스 기사 제목을 입력하세요"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">기사 URL <span className="text-red-400">*</span></label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent placeholder:text-gray-300"
              placeholder="https://example.com/news/article"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">출처 <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent placeholder:text-gray-300"
                placeholder="예: 연합뉴스"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">발행일시</label>
              <input
                type="datetime-local"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {result && (
          <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mt-4 text-xs font-medium ${
            result.success
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-600'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={result.success ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            </div>
            {result.message}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            onClick={handleClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors whitespace-nowrap cursor-pointer ${
              isSaving ? 'bg-teal-300 cursor-not-allowed' : 'bg-teal-500 hover:bg-teal-600'
            }`}
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin text-sm"></i>
                저장 중...
              </span>
            ) : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}
